// ZUSTAND→ATOM off-ramp (docs/projects/complete/web/zustand-to-effect-atom-migration.md)
// Status: converted
// atomKvsSync persisted atom; zustand `{state, version}` envelopes migrate
// forward through the schema transform below.
import { useAtomSet, useAtomValue } from '@effect/atom-react';
import { atomKvsSync } from '@listening/frontend/atoms/kvs';
import { Schema, SchemaTransformation, Struct } from 'effect';
import { useMemo } from 'react';

// Dev/staging-only: which conversion pipeline new uploads should request. `'default'` sends no
// override (the server config decides); `'v1'`/`'v2'` force that pipeline for this device. Client
// -only on purpose — this is per-device dev tooling, and the authoritative record of what ran is
// the `conversion_pipeline_version` field on the audio response.
// @see docs/projects/active/conversion-pipeline-v2/web-app-tooling.md
export type ConversionPipelinePreference = 'default' | 'v1' | 'v2';

// Dev/staging-only vision override for new uploads. `'default'` sends nothing — the server config
// decides (v2 now defaults vision OFF; v1 always runs vision). `'on'`/`'off'` force it for this
// device so teammates can dogfood both. Client-only, same rationale as `preference`.
export type ConversionVisionPreference = 'default' | 'on' | 'off';

// Dev/staging-only v2 variant switches, same 'default' = send-nothing semantics. All are no-ops
// on a v1 conversion (the server keeps v1 frozen).
export type ConversionOcrProviderPreference =
  | 'default'
  | 'azure'
  | 'paddle-modal'
  | 'paddleocr-vl-modal';

const stateSchema = Schema.Struct({
  preference: Schema.Literals(['default', 'v1', 'v2']),
  vision: Schema.Literals(['default', 'on', 'off']),
  ocrProvider: Schema.Literals([
    'default',
    'azure',
    'paddle-modal',
    'paddleocr-vl-modal',
  ]),
});

type ConversionPipelinePreferenceState = typeof stateSchema.Type;

const defaults: ConversionPipelinePreferenceState = {
  preference: 'default',
  vision: 'default',
  ocrProvider: 'default',
};

// zustand-persist wrote a `{state, version}` envelope (state possibly partial
// — fields were added over time). Decode merges over defaults; encode writes
// the full bare shape. See plan doc §C2.
const partialStateSchema = stateSchema.mapFields(
  Struct.map(Schema.optionalKey),
);
const persistedSchema = Schema.Union([
  Schema.Struct({ state: partialStateSchema, version: Schema.Int }),
  partialStateSchema,
]).pipe(
  Schema.decodeTo(
    stateSchema,
    SchemaTransformation.transform({
      decode: (stored) => ({
        ...defaults,
        ...('state' in stored ? stored.state : stored),
      }),
      encode: (state) => state,
    }),
  ),
);

const conversionPipelinePreferenceAtom = atomKvsSync({
  key: 'conversion-pipeline-preference',
  schema: persistedSchema,
  defaultValue: () => defaults,
});

type ConversionPipelinePreferenceStore = ConversionPipelinePreferenceState & {
  setPreference: (preference: ConversionPipelinePreference) => void;
  setVision: (vision: ConversionVisionPreference) => void;
  setOcrProvider: (ocrProvider: ConversionOcrProviderPreference) => void;
};

export function useConversionPipelinePreferenceStore(): ConversionPipelinePreferenceStore;
export function useConversionPipelinePreferenceStore<T>(
  selector: (store: ConversionPipelinePreferenceStore) => T,
): T;
export function useConversionPipelinePreferenceStore<T>(
  selector?: (store: ConversionPipelinePreferenceStore) => T,
): T | ConversionPipelinePreferenceStore {
  const setState = useAtomSet(conversionPipelinePreferenceAtom);
  const actions = useMemo(
    () => ({
      setPreference: (preference: ConversionPipelinePreference) => {
        setState((s) => ({ ...s, preference }));
      },
      setVision: (vision: ConversionVisionPreference) => {
        setState((s) => ({ ...s, vision }));
      },
      setOcrProvider: (ocrProvider: ConversionOcrProviderPreference) => {
        setState((s) => ({ ...s, ocrProvider }));
      },
    }),
    [setState],
  );
  // Derived-atom routing: the selector form is gated on the selected value
  // (zustand parity); the bare-call form maps to the whole store and
  // legitimately re-emits on every write.
  return useAtomValue(conversionPipelinePreferenceAtom, (state) => {
    const store = { ...state, ...actions };
    return selector ? selector(store) : store;
  });
}
